// Alert Rules management page.
// V2 design conformance: page wrapper (p:6 + Stack spacing:4), title (24/32
// medium 1000) + subtitle (14/21 700), select-based filter bar,
// MUI Table for the rules list, and the canonical Modal+Slide drawer
// pattern from Normal.js / auditlogs.js (NOT MUI Drawer).
//
// Backend: /api/user/v1/alert/rules (CRUD), /alert/rules/:id/test_fire,
// /alert/rules/stats (sparkline + 24h/7d counts). Channels set per-rule
// override the per-user NotificationPreference fallback.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box, Stack, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, Skeleton, IconButton, Button, Select, MenuItem,
  InputLabel, Switch, Tooltip, Modal, Slide, Dialog,
  DialogTitle, DialogContent, DialogActions, Checkbox, FormControl, FormControlLabel,
  InputAdornment, Alert} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
  PlayCircleOutline as PlayIcon,
  NotificationsActiveOutlined as BellIcon,
  Bolt as BoltIcon,
  MailOutlined as MailIcon,
  Webhook as WebhookIcon,
  ForumOutlined as ChatIcon,
  NotificationsOutlined as InAppIcon,
  CheckCircleOutline as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  AutoAwesome as SparkleIcon,
  DarkModeOutlined as MoonIcon,
  InfoOutlined as InfoIcon,
  MenuBookOutlined as BookIcon,
  TuneOutlined as TuneIcon,
} from "@mui/icons-material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import { Filter } from "lucide-react";

import axios from "../../../../utils/axios/v1/userAxios";
import colors from "../../../../utils/colors";
import { formatDate } from "../../../../utils/format";
import V2PageHeader from "../../../common/V2PageHeader";
import LayerdIcon from "../../../common/LayerdIcon";
import { CancelButton, PrimaryButton, primaryButtonSx } from "../../../common/Button";
import { SafeTextField, GreenSelectForm, GreenSwitch } from "../../../common/Greens";
import { useNotification } from "../../../../contexts/NotificationContext";
import { validateAlertRuleForm } from "../../../../utils/validation";
import { filterInputSx } from "../services/api_security/asecUi";

const brandBlue = colors.primary[300];
const switchSmallSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: colors.primary[300] },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: colors.primary[300],
    opacity: 0.35,
  },
  "& .MuiSwitch-track": {
    backgroundColor: colors.neutral[400],
    opacity: 0.35,
  },
};
const tableHeadSx =  { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] };
const tableBodySx =  { fontSize: 14, lineHeight: "21px", color: colors.neutral[800], verticalAlign: "top" };

const FILTER_CONTROL_HEIGHT = 36;

const EVENT_FILTER_BAR_SX = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  p: 2,
  backgroundColor: "white",
  borderRadius: "8px",
  border: `1px solid ${colors.neutral[200]}`,
};

const filterOutlinedRootSx = {
  height: FILTER_CONTROL_HEIGHT,
  minHeight: FILTER_CONTROL_HEIGHT,
  maxHeight: FILTER_CONTROL_HEIGHT,
  borderRadius: "8px",
  fontSize: 14,
  backgroundColor: "#fff",
  boxSizing: "border-box",
  "& fieldset": { borderColor: colors.neutral[200] },
  "&:hover fieldset": { borderColor: colors.neutral[300] },
  "&.Mui-focused fieldset": { borderColor: brandBlue },
};

const filterSelectSx = {
  height: FILTER_CONTROL_HEIGHT,
  "& .MuiOutlinedInput-root": filterOutlinedRootSx,
  "& .MuiSelect-select": {
    py: 0,
    pl: 1.5,
    pr: 4,
    height: `${FILTER_CONTROL_HEIGHT}px !important`,
    minHeight: `${FILTER_CONTROL_HEIGHT}px !important`,
    fontSize: 14,
    lineHeight: "20px",
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
  },
  "& .MuiSelect-icon": {
    top: "50%",
    transform: "translateY(-50%)",
    right: 8,
  },
};

const filterPrimaryButtonSx = {
  ...primaryButtonSx,
  height: FILTER_CONTROL_HEIGHT,
  minHeight: FILTER_CONTROL_HEIGHT,
  minWidth: 132,
  px: 2.5,
  fontSize: 14,
  lineHeight: "21px",
  whiteSpace: "nowrap",
  "& .MuiButton-startIcon": { mr: 0.75 },
};

const activityStatLabelSx = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: colors.neutral[600],
  lineHeight: 1.3,
};

const activityStatValueSx = {
  display: "block",
  mt: 0.75,
  fontSize: 20,
  fontWeight: 600,
  lineHeight: "26px",
  letterSpacing: "-0.01em",
  color: colors.neutral[1000],
  fontVariantNumeric: "tabular-nums",
};

const SEVERITY_FILTER_LABELS = {
  all: "All",
  critical: "Critical",
  high: "High",
  medium: "Medium",
  low: "Low",
};

const STATE_FILTER_LABELS = {
  all: "All",
  firing: "Firing",
  disabled: "Disabled",
};

const TYPE_FILTER_LABELS = {
  all: "All",
  custom: "Custom",
  builtin: "Built-in",
};

const GROUP_FILTER_LABELS = {
  severity: "Severity",
  kind: "Type",
  state: "State",
  none: "None",
};

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------
const ALERT_TYPES = [
  { value: "WAF_SPIKE",         label: "WAF attack spike",       fields: ["threshold", "window_minutes"], metric: "attack_events" },
  { value: "SMART_PARSE_SPIKE", label: "Smart Parse spike",      fields: ["threshold", "window_minutes"], metric: "smart_parse_events" },
  { value: "BOT_SURGE",         label: "Bad-bot surge",          fields: ["threshold", "window_minutes"], metric: "bot.bad_events" },
  { value: "RL_SPIKE",          label: "Rate-limit spike",       fields: ["threshold", "window_minutes"], metric: "rate_limited" },
  { value: "DDOS_DETECTED",     label: "DDoS attack detected",   fields: [],                              metric: "ad_engine.classification" },
  { value: "ENGINE_HEALTH",     label: "Engine health degraded", fields: ["statuses"],                    metric: "engine.state" },
  { value: "WSS_DROP",          label: "Security score drop",    fields: ["drop_threshold"],              metric: "security_score.delta_1h" },
  { value: "CERT_EXPIRY",       label: "SSL certificate expiry", fields: ["days_before"],                 metric: "cert.days_to_expiry" },
  { value: "ANOMALY",           label: "Statistical anomaly",    fields: [],                              metric: "anomaly.zscore" },
];

// Severity color pairs — match the WAFEvent.js convention (tinted bg + ink).
const SEVERITY_SPEC = {
  critical: { label: "Critical", bg: "#FEE2E2", ink: "#B91C1C", dot: "#DC2626" },
  high:     { label: "High",     bg: "#FFEDD5", ink: "#C2410C", dot: "#EA580C" },
  medium:   { label: "Medium",   bg: "#FEF3C7", ink: "#B45309", dot: "#D97706" },
  low:      { label: "Low",      bg: "#DCFCE7", ink: "#15803D", dot: "#16A34A" },
  info:     { label: "Info",     bg: colors.neutral[100], ink: colors.neutral[700], dot: colors.neutral[500] },
};

const CH_META = {
  IN_APP:  { Icon: InAppIcon,   label: "In-app",  dest: "Bell dropdown · all org users" },
  EMAIL:   { Icon: MailIcon,    label: "Email",   dest: "Configured email recipients (or org admin)" },
  WEBHOOK: { Icon: WebhookIcon, label: "Webhook", dest: "HTTP POST with HMAC-SHA256" },
  SLACK:   { Icon: ChatIcon,    label: "Slack",   dest: "Per-org Slack webhook config" },
};
const ALL_CHANNELS = ["IN_APP", "EMAIL", "WEBHOOK", "SLACK"];
const ENGINE_STATUSES = ["DEGRADED", "UNHEALTHY", "OFFLINE"];

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------
function sevSpec(s)   { return SEVERITY_SPEC[s] || SEVERITY_SPEC.low; }
function typeLabel(t) { return ALERT_TYPES.find((x) => x.value === t)?.label || t; }
function typeMetric(t){ return ALERT_TYPES.find((x) => x.value === t)?.metric || (t || "").toLowerCase(); }

function triggerExpr(rule) {
  const c = rule.conditions || {};
  const m = typeMetric(rule.alertType);
  if (["WAF_SPIKE", "SMART_PARSE_SPIKE", "BOT_SURGE", "RL_SPIKE"].includes(rule.alertType)) {
    return `${m} > ${c.threshold ?? "?"} in ${c.window_minutes ?? "?"}m`;
  }
  if (rule.alertType === "WSS_DROP") return `${m} <= -${c.drop_threshold ?? "?"}`;
  if (rule.alertType === "CERT_EXPIRY") return `${m} in [${(c.days_before || []).join(", ") || "?"}]`;
  if (rule.alertType === "ENGINE_HEALTH") {
    return `${m} in [${(c.statuses || []).map((s) => s.toLowerCase()).join(", ") || "any"}]`;
  }
  if (rule.alertType === "DDOS_DETECTED") return `${m} = ddos`;
  if (rule.alertType === "ANOMALY")       return `${m} > 3.0`;
  return JSON.stringify(c);
}

function scopeLabel(rule) {
  if (!rule.siteIds || rule.siteIds.length === 0) return "all domains";
  if (rule.siteIds.length === 1) return rule.siteIds[0];
  return `${rule.siteIds.length} domains`;
}

// "Firing now" heuristic: lastFiredAt within cooldown window. Good visual proxy
// until we add an explicit firing-state endpoint.
function isFiring(rule) {
  if (!rule.lastFiredAt) return false;
  const ms = Date.now() - new Date(rule.lastFiredAt).getTime();
  return ms <= (rule.cooldownMinutes || 30) * 60 * 1000;
}

function relativeTime(d) {
  if (!d) return null;
  const t = typeof d === "string" ? new Date(d).getTime() : d.getTime();
  if (Number.isNaN(t)) return null;
  const diff = Math.max(0, Date.now() - t);
  const s = Math.floor(diff / 1000);
  if (s < 60)  return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(t).toLocaleDateString();
}

function shortAbs(d) {
  if (!d) return null;
  const formatted = formatDate(d);
  return formatted || null;
}

function emptyRule() {
  return {
    name: "",
    description: "",
    alertType: "WAF_SPIKE",
    severity: "medium",
    conditions: { threshold: 50, window_minutes: 5 },
    cooldownMinutes: 30,
    siteIds: [],
    channels: ["IN_APP", "EMAIL"],
    notifyEmails: [],
    webhookUrl: "",
    webhookSecret: "",
    integrationDestinationIds: [],
    enabled: true,
  };
}

// ---------------------------------------------------------------------------
// Small visual primitives — all in V2 token system
// ---------------------------------------------------------------------------
function SeverityChip({ s }) {
  const spec = sevSpec(s);
  return (
    <Chip size="small" label={spec.label}
      sx={{
        height: 22, fontSize: 12, fontWeight: 500,
        backgroundColor: spec.bg, color: spec.ink,
        border: "none",
      }} />
  );
}

function ChannelChip({ k }) {
  const m = CH_META[k];
  if (!m) return null;
  const Icon = m.Icon;
  return (
    <Tooltip title={m.label}>
      <Chip size="small" icon={<Icon sx={{ fontSize: 14 }} />} label={m.label}
        sx={{
          height: 22, fontSize: 11.5,
          backgroundColor: colors.primary[100], color: colors.primary[300],
          border: "none",
          "& .MuiChip-icon": { color: colors.primary[300], marginLeft: "6px", marginRight: "-4px" },
          "& .MuiChip-label": { paddingLeft: "8px", paddingRight: "10px" },
        }} />
    </Tooltip>
  );
}

function ChannelsCell({ list }) {
  if (!list || list.length === 0) {
    return (
      <Tooltip title="Falls back to per-user notification preferences">
        <Typography sx={{ fontSize: 13, color: colors.neutral[500] }}>user prefs</Typography>
      </Tooltip>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
      {list.map((k) => <ChannelChip key={k} k={k} />)}
    </Stack>
  );
}

function KindTag({ builtin }) {
  return (
    <Chip size="small" label={builtin ? "built-in" : "custom"}
      sx={{
        height: 20, fontSize: 11, fontWeight: 500,
        backgroundColor: builtin ? colors.neutral[100] : colors.primary[100],
        color:           builtin ? colors.neutral[700] : colors.primary[300],
        border: "none",
      }} />
  );
}

function FiringTag() {
  return (
    <Chip size="small" icon={<BoltIcon sx={{ fontSize: 12 }} />} label="firing now"
      sx={{
        height: 20, fontSize: 11, fontWeight: 500,
        backgroundColor: "#FEE2E2", color: "#B91C1C", border: "none",
        animation: "alertPulse 1.6s ease-in-out infinite",
        "& .MuiChip-icon": { color: "#B91C1C", marginLeft: "4px", marginRight: "-4px" },
      }} />
  );
}

// 14-bar sparkline fed by stats.sparkline14d.
function FireSpark({ data, w = 100, h = 22, color }) {
  const bins = (data && data.length === 14) ? data : new Array(14).fill(0);
  const max = Math.max(...bins, 1);
  const bw = w / 14;
  const c = color || colors.red[100];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} style={{ display: "block" }}>
      {bins.map((v, i) => {
        const bh = Math.max(2, (v / max) * (h - 2));
        const empty = v === 0;
        return (
          <rect key={i} x={i * bw + 0.5} y={h - bh}
            width={bw - 1} height={bh} rx="1"
            fill={empty ? colors.neutral[200] : c}
            opacity={empty ? 0.55 : 0.9} />
        );
      })}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// KPI strip — 4 cards using the standard card container
// ---------------------------------------------------------------------------
function Kpis({ rules, stats, firingMap }) {
  const firingCount = useMemo(() => {
    let n = 0;
    if (firingMap) for (const v of firingMap.values()) if (v) n++;
    return n;
  }, [firingMap]);
  const enabledCount = rules.filter((r) => r.enabled).length;
  const disabledCount = rules.length - enabledCount;
  const customCount = rules.filter((r) => !r.builtin).length;
  const builtinCount = rules.length - customCount;
  const total24h = rules.reduce((s, r) => s + (stats[r.id]?.fires24h || 0), 0);
  const total7d  = rules.reduce((s, r) => s + (stats[r.id]?.fires7d  || 0), 0);

  const Card = ({ children, accent }) => (
    <Box sx={{
      backgroundColor: "#fff",
      borderRadius: "12px",
      border: `1px solid ${colors.neutral[200]}`,
      p: 3,
      position: "relative",
      overflow: "hidden",
      ...(accent ? {
        "&::before": {
          content: '""', position: "absolute", left: 0, top: 0, bottom: 0,
          width: 3, backgroundColor: accent,
        },
      } : {}),
    }}>{children}</Box>
  );

  const Label = ({ children }) => (
    <Typography sx={{
fontSize: 12, fontWeight: 500,
      color: colors.neutral[600], textTransform: "uppercase", letterSpacing: "0.05em",
      mb: 1,
    }}>{children}</Typography>
  );

  const Value = ({ children, color }) => (
    <Typography sx={{
fontSize: 28, lineHeight: "32px",
      fontWeight: 700, letterSpacing: "-0.01em",
      fontVariantNumeric: "tabular-nums",
      color: color || colors.neutral[1000],
    }}>{children}</Typography>
  );

  const Sub = ({ children, icon: Icon, color }) => (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 1 }}>
      {Icon && <Icon sx={{ fontSize: 13, color: color || colors.neutral[600] }} />}
      <Typography sx={{ fontSize: 12, color: color || colors.neutral[700] }}>{children}</Typography>
    </Stack>
  );

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
      <Card accent={firingCount > 0 ? colors.red[100] : null}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Label>Firing now</Label>
          {firingCount > 0 && (
            <Chip size="small" label="active"
              sx={{ height: 20, fontSize: 11, backgroundColor: "#FEE2E2", color: "#B91C1C", border: "none" }} />
          )}
        </Stack>
        <Value color={firingCount > 0 ? colors.red[100] : colors.neutral[1000]}>{firingCount}</Value>
        <Sub icon={BoltIcon} color={firingCount > 0 ? colors.red[100] : colors.neutral[700]}>
          {firingCount > 0 ? `${firingCount} rule${firingCount === 1 ? "" : "s"} in cooldown` : "No active alerts"}
        </Sub>
      </Card>

      <Card>
        <Label>Total fires · 24h</Label>
        <Value>{total24h}</Value>
        <Sub icon={TrendingUpIcon}>{total7d} this week</Sub>
      </Card>

      <Card>
        <Label>Enabled rules</Label>
        <Value>
          {enabledCount}
          <Box component="span" sx={{ color: colors.neutral[600], fontSize: 16, fontWeight: 500 }}>{` / ${rules.length}`}</Box>
        </Value>
        <Sub icon={CheckCircleIcon} color={disabledCount === 0 ? colors.green[300] : colors.neutral[700]}>
          {disabledCount === 0 ? "All armed" : `${disabledCount} disabled`}
        </Sub>
      </Card>

      <Card>
        <Label>Custom rules</Label>
        <Value>
          {customCount}
          <Box component="span" sx={{ color: colors.neutral[600], fontSize: 16, fontWeight: 500 }}>{` / ${builtinCount} built-in`}</Box>
        </Value>
        <Sub icon={SparkleIcon} color={brandBlue}>Build your own with channel routing</Sub>
      </Card>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Filter bar — select-based row (Threat Insights / License pattern)
// ---------------------------------------------------------------------------
function FilterBar({ filter, setFilter, group, setGroup, onCreate }) {
  const hasActiveFilters = Boolean(
    filter.q ||
    filter.sev !== "all" ||
    filter.state !== "all" ||
    filter.kind !== "all" ||
    group !== "severity"
  );

  const clearAll = () => {
    setFilter({ sev: "all", state: "all", kind: "all", q: "" });
    setGroup("severity");
  };

  return (
    <Box sx={EVENT_FILTER_BAR_SX}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 1.5 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          flexWrap="wrap"
          sx={{ gap: 1.5, flex: 1 }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <LayerdIcon icon={Filter} size={18} color="primary" />
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: colors.neutral[800] }}>
              Filters
            </Typography>
          </Stack>

          <SafeTextField
            size="small"
            variant="outlined"
            placeholder="Search by name, description, or type…"
            value={filter.q}
            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
            sx={{
              ...filterInputSx,
              width: { xs: "100%", sm: 280 },
              flex: { sm: "1 1 280px" },
              maxWidth: { sm: 360 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: colors.neutral[600] }} />
                </InputAdornment>
              ),
            }}
          />

          <Select
            value={filter.sev}
            onChange={(e) => setFilter({ ...filter, sev: e.target.value })}
            sx={{ ...filterSelectSx, minWidth: 140 }}
            renderValue={(v) => `Severity: ${SEVERITY_FILTER_LABELS[v] || v}`}
          >
            {Object.entries(SEVERITY_FILTER_LABELS).map(([k, lbl]) => (
              <MenuItem key={k} value={k} sx={{ fontSize: 14 }}>{lbl}</MenuItem>
            ))}
          </Select>

          <Select
            value={filter.state}
            onChange={(e) => setFilter({ ...filter, state: e.target.value })}
            sx={{ ...filterSelectSx, minWidth: 130 }}
            renderValue={(v) => `State: ${STATE_FILTER_LABELS[v] || v}`}
          >
            {Object.entries(STATE_FILTER_LABELS).map(([k, lbl]) => (
              <MenuItem key={k} value={k} sx={{ fontSize: 14 }}>{lbl}</MenuItem>
            ))}
          </Select>

          <Select
            value={filter.kind}
            onChange={(e) => setFilter({ ...filter, kind: e.target.value })}
            sx={{ ...filterSelectSx, minWidth: 130 }}
            renderValue={(v) => `Type: ${TYPE_FILTER_LABELS[v] || v}`}
          >
            {Object.entries(TYPE_FILTER_LABELS).map(([k, lbl]) => (
              <MenuItem key={k} value={k} sx={{ fontSize: 14 }}>{lbl}</MenuItem>
            ))}
          </Select>

          <Select
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            sx={{ ...filterSelectSx, minWidth: 140 }}
            renderValue={(v) => `Group: ${GROUP_FILTER_LABELS[v] || v}`}
          >
            {Object.entries(GROUP_FILTER_LABELS).map(([k, lbl]) => (
              <MenuItem key={k} value={k} sx={{ fontSize: 14 }}>{lbl}</MenuItem>
            ))}
          </Select>

          {hasActiveFilters && (
            <Chip
              label="Clear All"
              onClick={clearAll}
              sx={{
                height: 36,
                fontSize: 14,
                borderRadius: "8px",
                backgroundColor: "#fff",
                border: `1px solid ${colors.neutral[200]}`,
                color: colors.neutral[800],
                "&:hover": {
                  borderColor: colors.red[100],
                  color: colors.red[100],
                },
              }}
            />
          )}
        </Stack>

        <Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
          <PrimaryButton
            startIcon={<AddIcon />}
            onClick={onCreate}
            sx={{ ...filterPrimaryButtonSx, fontWeight: 600 }}
          >
            New rule
          </PrimaryButton>
        </Stack>
      </Stack>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Conditions editor (used inside the drawer)
// ---------------------------------------------------------------------------
function ConditionsEditor({ alertType, conditions, setConditions, fieldErrors = {}, showErrors = false }) {
  const cfg = ALERT_TYPES.find((t) => t.value === alertType);
  if (!cfg || cfg.fields.length === 0) {
    return (
      <Typography sx={{ fontSize: 13, color: colors.neutral[700] }}>
        No additional conditions — this alert type fires on every qualifying event.
      </Typography>
    );
  }
  const showErr = (key) => showErrors && fieldErrors[key];
  return (
    <Stack spacing={2}>
      {cfg.fields.includes("threshold") && (
        <SafeTextField label="Threshold (event count)" type="number" size="small" fullWidth
          value={conditions.threshold ?? ""}
          error={Boolean(showErr("threshold"))}
          helperText={showErr("threshold") || "Fires when this many qualifying events occur in the window"}
          onChange={(e) => setConditions({ ...conditions, threshold: parseInt(e.target.value, 10) || 0 })} />
      )}
      {cfg.fields.includes("window_minutes") && (
        <SafeTextField label="Window (minutes)" type="number" size="small" fullWidth
          value={conditions.window_minutes ?? ""}
          error={Boolean(showErr("window_minutes"))}
          helperText={showErr("window_minutes") || "Sliding window evaluated every 60 seconds"}
          onChange={(e) => setConditions({ ...conditions, window_minutes: parseInt(e.target.value, 10) || 0 })} />
      )}
      {cfg.fields.includes("drop_threshold") && (
        <SafeTextField label="Score drop threshold (points)" type="number" size="small" fullWidth
          value={conditions.drop_threshold ?? ""}
          error={Boolean(showErr("drop_threshold"))}
          helperText={showErr("drop_threshold") || "Fires when the security score drops by this many points or more"}
          onChange={(e) => setConditions({ ...conditions, drop_threshold: parseInt(e.target.value, 10) || 0 })} />
      )}
      {cfg.fields.includes("days_before") && (
        <SafeTextField label="Days before expiry" size="small" fullWidth
          value={(conditions.days_before || []).join(", ")}
          error={Boolean(showErr("days_before"))}
          helperText={showErr("days_before") || "Comma-separated list, e.g. 7, 3, 1, 0"}
          onChange={(e) => setConditions({
            ...conditions,
            days_before: e.target.value.split(",").map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n)),
          })} />
      )}
      {cfg.fields.includes("statuses") && (
        <Stack spacing={1}>
          <Typography sx={ { fontSize: 14, color: colors.neutral[1000] }}>Engine statuses to alert on</Typography>
          {showErr("statuses") && (
            <Typography sx={{ fontSize: 12, color: colors.red[100] }}>{fieldErrors.statuses}</Typography>
          )}
          <Stack direction="row" spacing={1}>
            {ENGINE_STATUSES.map((s) => {
              const selected = (conditions.statuses || []).includes(s);
              return (
                <Chip key={s} label={s} size="small" clickable
                  onClick={() => {
                    const next = selected
                      ? (conditions.statuses || []).filter((x) => x !== s)
                      : [...(conditions.statuses || []), s];
                    setConditions({ ...conditions, statuses: next });
                  }}
                  sx={selected
                    ? { backgroundColor: brandBlue, color: "#fff", "&:hover": { backgroundColor: "#4959ee" } }
                    : { backgroundColor: "#fff", color: colors.neutral[700], border: `1px solid ${colors.neutral[200]}` }
                  } />
              );
            })}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Section card used in the drawer body (mirrors auditlogs.js)
// ---------------------------------------------------------------------------
function SectionCard({ icon: Icon, title, children }) {
  return (
    <Box sx={{
      backgroundColor: colors.neutral[50], borderRadius: "8px",
      border: `1px solid ${colors.neutral[200]}`, overflow: "hidden",
    }}>
      <Stack direction="row" alignItems="center" spacing={1.5}
        sx={{ p: 2, borderBottom: `1px solid ${colors.neutral[200]}` }}>
        {Icon && <Icon sx={{ width: 20, height: 20, color: brandBlue }} />}
        <Typography sx={ { fontSize: 16, fontWeight: "medium", color: colors.neutral[1000] }}>
          {title}
        </Typography>
      </Stack>
      <Box sx={{ p: 3 }}>{children}</Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Drawer — canonical Modal+Slide pattern from auditlogs.js / Normal.js
// ---------------------------------------------------------------------------
function RuleDrawer({ rule, stats, open, onClose, onSave, onDelete, onTestFire, busyId }) {
  const [edit, setEdit] = useState({ ...emptyRule(), ...(rule || {}) });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [destinations, setDestinations] = useState([]);
  const [touched, setTouched] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    setEdit({ ...emptyRule(), ...(rule || {}) });
    setErr(null);
    setTouched({});
    setSubmitAttempted(false);
  }, [rule]);

  const validation = useMemo(() => validateAlertRuleForm(edit), [edit]);
  const showErr = (field) => (submitAttempted || touched[field]) && validation.errors[field];

  useEffect(() => {
    // Load integration destinations once per drawer open. Errors are
    // silently swallowed (the multi-select just shows empty) because
    // a fresh org with no destinations should still be able to edit
    // alert rules.
    if (!open) return;
    let cancelled = false;
    axios.get("integrations").then((r) => {
      if (!cancelled) setDestinations(r.data.items || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  if (!rule) return null;

  const isEdit = Boolean(rule.id);
  const wantsEmail   = (edit.channels || []).includes("EMAIL");
  const wantsWebhook = (edit.channels || []).includes("WEBHOOK");
  const fires7d  = stats?.fires7d  || 0;
  const fires24h = stats?.fires24h || 0;
  const spark    = stats?.sparkline14d || new Array(14).fill(0);

  const toggleChannel = (c) => {
    const cur = edit.channels || [];
    setEdit({ ...edit, channels: cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c] });
  };

  const onSubmit = async () => {
    setSubmitAttempted(true);
    const result = validateAlertRuleForm(edit);
    if (!result.valid) return;

    setErr(null);
    setSaving(true);
    try {
      const payload = {
        name: edit.name,
        description: edit.description || null,
        alertType: edit.alertType,
        severity: edit.severity,
        conditions: edit.conditions,
        cooldownMinutes: parseInt(edit.cooldownMinutes, 10) || 30,
        siteIds: edit.siteIds || [],
        channels: edit.channels || [],
        notifyEmails: edit.notifyEmails || [],
        webhookUrl: edit.webhookUrl || null,
        integrationDestinationIds: edit.integrationDestinationIds || [],
      };
      if (edit.webhookSecret && edit.webhookSecret.length > 0) {
        payload.webhookSecret = edit.webhookSecret;
      }
      const r = isEdit
        ? await axios.patch(`alert/rules/${rule.id}`, payload)
        : await axios.put(`alert/rules`, payload);
      onSave(r.data, isEdit);
    } catch (e) {
      setErr(e.response?.data?.message || e.message || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Box sx={{
          position: "fixed", top: 0, right: 0,
          width: "40%", minWidth: 560,
          height: "100vh",
          backgroundColor: "#fff",
          boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
          zIndex: 1300,
          borderTopLeftRadius: "8px",
          outline: "none", border: "none",
          display: "flex", flexDirection: "column",
        }}>
          {/* Drawer header */}
          <Stack direction="row" alignItems="center" justifyContent="space-between"
            sx={{ p: 4, borderBottom: `1px solid ${colors.neutral[200]}`, flexShrink: 0 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{
                width: 48, height: 48, borderRadius: "8px",
                backgroundColor: "#EEF0FE",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <BellIcon sx={{ width: 28, height: 28, color: brandBlue }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={ { fontSize: 20, fontWeight: "medium", color: colors.neutral[1000] }}>
                  {isEdit ? rule.name : "New alert rule"}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
                  {isEdit && <KindTag builtin={rule.builtin} />}
                  {isEdit && <SeverityChip s={rule.severity} />}
                  {isEdit && (
                    <Typography sx={ { fontSize: 12, color: colors.neutral[600] }}>
                      {scopeLabel(rule)} · fired {fires7d} {fires7d === 1 ? "time" : "times"} this week
                    </Typography>
                  )}
                </Stack>
              </Box>
            </Stack>
            <IconButton onClick={onClose}>
              <CloseIcon sx={{ width: 24, height: 24, color: colors.neutral[600] }} />
            </IconButton>
          </Stack>

          {/* Drawer body — scrollable */}
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ReactPerfectScrollbar>
              <Box sx={{ p: 4 }}>
                {err && (
                  <Box sx={{
                    backgroundColor: "#FEE2E2", border: "1px solid #FCA5A5",
                    color: "#B91C1C", borderRadius: "8px", p: 2, mb: 2,
                    fontSize: 13,
                  }}>{err}</Box>
                )}

                <Stack spacing={3}>
                  {!rule.builtin && (
                    <SectionCard icon={TuneIcon} title="Basics">
                      <Stack spacing={2}>
                        <SafeTextField label="Name" size="small" fullWidth required
                          value={edit.name}
                          error={Boolean(showErr("name"))}
                          helperText={showErr("name") || undefined}
                          onBlur={() => setTouched((prev) => ({ ...prev, name: true }))}
                          onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
                        <SafeTextField label="Description" size="small" fullWidth multiline minRows={2}
                          value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })} />
                      </Stack>
                    </SectionCard>
                  )}

                  <SectionCard icon={BoltIcon} title="Trigger">
                    <Stack spacing={2.5}>
                      <Stack direction="row" spacing={2}>
                        <GreenSelectForm variant="outlined" size="small" fullWidth>
                          <InputLabel>Alert type</InputLabel>
                          <Select value={edit.alertType} label="Alert type"
                            disabled={rule.builtin}
                            onChange={(e) => {
                              const next = ALERT_TYPES.find((t) => t.value === e.target.value);
                              const c = {};
                              if (next?.fields.includes("threshold"))      c.threshold = 50;
                              if (next?.fields.includes("window_minutes")) c.window_minutes = 5;
                              if (next?.fields.includes("drop_threshold")) c.drop_threshold = 10;
                              if (next?.fields.includes("days_before"))    c.days_before = [7, 3, 1, 0];
                              if (next?.fields.includes("statuses"))       c.statuses = ["DEGRADED", "UNHEALTHY", "OFFLINE"];
                              setEdit({ ...edit, alertType: e.target.value, conditions: c });
                            }}>
                            {ALERT_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                          </Select>
                        </GreenSelectForm>
                        <GreenSelectForm variant="outlined" size="small" sx={{ minWidth: 140 }}>
                          <InputLabel>Severity</InputLabel>
                          <Select value={edit.severity} label="Severity"
                            onChange={(e) => setEdit({ ...edit, severity: e.target.value })}>
                            {["critical", "high", "medium", "low"].map((s) => (
                              <MenuItem key={s} value={s}>{sevSpec(s).label}</MenuItem>
                            ))}
                          </Select>
                        </GreenSelectForm>
                      </Stack>
                      <ConditionsEditor
                        alertType={edit.alertType}
                        conditions={edit.conditions || {}}
                        setConditions={(c) => setEdit({ ...edit, conditions: c })}
                        fieldErrors={validation.errors}
                        showErrors={submitAttempted} />
                      <Box sx={{
                        backgroundColor: "#fff", border: `1px solid ${colors.neutral[200]}`,
                        borderRadius: "6px", p: 1.5,
                      }}>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <InfoIcon sx={{ fontSize: 16, color: brandBlue }} />
                          <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>
                            Resolved expression: <Box component="span" sx={{ fontFamily: "monospace", color: colors.neutral[1000] }}>{triggerExpr(edit)}</Box>
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>
                  </SectionCard>

                  <SectionCard icon={InAppIcon} title="Delivery channels">
                    <Stack spacing={1.5}>
                      <Typography sx={{ fontSize: 13, color: colors.neutral[700] }}>
                        {(edit.channels || []).length === 0
                          ? "No channels — this rule will fall back to per-user notification preferences."
                          : `${edit.channels.length} of ${ALL_CHANNELS.length} channels active.`}
                      </Typography>
                      {ALL_CHANNELS.map((k) => {
                        const meta = CH_META[k];
                        const Icon = meta.Icon;
                        const on = (edit.channels || []).includes(k);
                        return (
                          <Stack key={k} direction="row" alignItems="center" spacing={2}
                            sx={{
                              p: 1.5, borderRadius: "8px",
                              border: `1px solid ${on ? brandBlue : colors.neutral[200]}`,
                              backgroundColor: on ? colors.primary[100] : "#fff",
                            }}>
                            <Box sx={{
                              width: 36, height: 36, borderRadius: "8px",
                              backgroundColor: on ? "#fff" : colors.neutral[50],
                              border: `1px solid ${on ? colors.primary[100] : colors.neutral[200]}`,
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                              <Icon sx={{ fontSize: 18, color: on ? brandBlue : colors.neutral[700] }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={ { fontSize: 14, fontWeight: 500, color: colors.neutral[1000] }}>
                                {meta.label}
                              </Typography>
                              <Typography sx={{ fontFamily: "monospace", fontSize: 11.5, color: colors.neutral[600] }}>
                                {meta.dest}
                              </Typography>
                            </Box>
                            <GreenSwitch checked={on} onChange={() => toggleChannel(k)} />
                          </Stack>
                        );
                      })}
                      {wantsEmail && (
                        <SafeTextField label="Email recipients (comma-separated)" sanitize={false} size="small" fullWidth sx={{ mt: 1.5 }}
                          value={(edit.notifyEmails || []).join(", ")}
                          error={Boolean(showErr("notifyEmails"))}
                          helperText={showErr("notifyEmails") || "Leave blank to send to the organisation admin"}
                          onBlur={() => setTouched((prev) => ({ ...prev, notifyEmails: true }))}
                          onChange={(e) => setEdit({
                            ...edit,
                            notifyEmails: e.target.value.split(",").map((s) => s.trim()).filter(Boolean),
                          })} />
                      )}
                      {wantsWebhook && (
                        <Stack spacing={1.5} sx={{ mt: 1 }}>
                          <SafeTextField label="Webhook URL" sanitize={false} size="small" fullWidth required
                            value={edit.webhookUrl || ""}
                            error={Boolean(showErr("webhookUrl"))}
                            helperText={showErr("webhookUrl") || "POST with JSON body, schema = layerd.alert.v1"}
                            onBlur={() => setTouched((prev) => ({ ...prev, webhookUrl: true }))}
                            onChange={(e) => setEdit({ ...edit, webhookUrl: e.target.value })}
                            placeholder="https://hooks.example.com/path" />
                          <SafeTextField
                            label={isEdit && edit.webhookSecretSet
                              ? "Replace webhook secret (leave blank to keep existing)"
                              : "Webhook secret (optional)"}
                            size="small" fullWidth type="password"
                            value={edit.webhookSecret || ""}
                            onChange={(e) => setEdit({ ...edit, webhookSecret: e.target.value })}
                            helperText="If set, requests include X-Layerd-Signature: sha256=<hmac>" />
                        </Stack>
                      )}
                    </Stack>
                  </SectionCard>

                  <SectionCard icon={InAppIcon} title="Integration destinations">
                    <Stack spacing={1.5}>
                      <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>
                        Route this rule to PagerDuty, Slack, Jira, ServiceNow, Linear, Zendesk, Freshdesk, Opsgenie, VictorOps, FireHydrant, Teams, Discord, Webex, Mattermost, or Datadog. Manage destinations in
                        <Box component="a" href="/v2/settings/webhooks" sx={{ ml: 0.5, color: colors.neutral[700], textDecoration: "underline" }}>Settings → Integrations</Box>.
                      </Typography>
                      {destinations.length === 0 ? (
                        <Alert severity="info" sx={{ fontSize: 12 }}>
                          No destinations configured yet. Add one in Settings → Integrations.
                        </Alert>
                      ) : (
                        <FormControl size="small" fullWidth>
                          <InputLabel>Destinations</InputLabel>
                          <Select
                            multiple
                            label="Destinations"
                            value={edit.integrationDestinationIds || []}
                            onChange={(e) => setEdit({ ...edit, integrationDestinationIds: e.target.value })}
                            renderValue={(selected) => (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {selected.map((id) => {
                                  const d = destinations.find((x) => x.id === id);
                                  return (
                                    <Chip key={id} size="small"
                                      label={d ? `${d.name} (${d.kind})` : id}
                                      sx={{ height: 22 }} />
                                  );
                                })}
                              </Box>
                            )}>
                            {destinations.map((d) => (
                              <MenuItem key={d.id} value={d.id} disabled={!d.enabled}>
                                {d.name} — {d.kind}{!d.enabled ? " (disabled)" : ""}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Stack>
                  </SectionCard>

                  <SectionCard icon={MoonIcon} title="Suppression">
                    <Stack spacing={2}>
                      <GreenSelectForm variant="outlined" size="small" fullWidth error={Boolean(showErr("cooldownMinutes"))}>
                        <InputLabel>Cooldown window</InputLabel>
                        <Select value={edit.cooldownMinutes} label="Cooldown window"
                          onBlur={() => setTouched((prev) => ({ ...prev, cooldownMinutes: true }))}
                          onChange={(e) => setEdit({ ...edit, cooldownMinutes: parseInt(e.target.value, 10) })}>
                          {[1, 5, 15, 30, 60, 360, 1440].map((m) => (
                            <MenuItem key={m} value={m}>{m < 60 ? `${m} minute${m === 1 ? "" : "s"}` : `${m / 60} hour${m === 60 ? "" : "s"}`}</MenuItem>
                          ))}
                        </Select>
                      </GreenSelectForm>
                      {showErr("cooldownMinutes") && (
                        <Typography sx={{ fontSize: 12, color: colors.red[100], mx: 1.75 }}>
                          {validation.errors.cooldownMinutes}
                        </Typography>
                      )}
                      <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>
                        Suppress repeats of the same alert within this window. The 60s evaluation cycle skips rules in cooldown.
                      </Typography>
                    </Stack>
                  </SectionCard>

                  {isEdit && (
                    <SectionCard icon={TrendingUpIcon} title="Activity (last 14 days)">
                      <Stack spacing={2}>
                        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
                          {[
                            { label: "Fires · 14d", value: spark.reduce((s, n) => s + n, 0) },
                            { label: "Avg / day",   value: (spark.reduce((s, n) => s + n, 0) / 14).toFixed(1) },
                            { label: "Days fired",  value: `${spark.filter((n) => n > 0).length} / 14` },
                          ].map((stat) => (
                            <Box key={stat.label} sx={{
                              backgroundColor: "#fff", border: `1px solid ${colors.neutral[200]}`,
                              borderRadius: "6px", p: 1.5,
                            }}>
                              <Typography component="span" sx={activityStatLabelSx}>
                                {stat.label}
                              </Typography>
                              <Typography component="span" sx={activityStatValueSx}>
                                {stat.value}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: 12, color: colors.neutral[600], textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
                            14-day fire density
                          </Typography>
                          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(14, 1fr)", gap: "4px", height: 60, alignItems: "end" }}>
                            {spark.map((n, i) => {
                              const max = Math.max(...spark, 1);
                              const h = n === 0 ? 4 : Math.max(6, (n / max) * 56);
                              return (
                                <Tooltip key={i} title={`${n} fire${n === 1 ? "" : "s"}`}>
                                  <Box sx={{
                                    backgroundColor: n === 0 ? colors.neutral[200] : colors.red[100],
                                    opacity: n === 0 ? 0.5 : 0.9,
                                    borderRadius: "2px", height: `${h}px`, cursor: "pointer",
                                  }} />
                                </Tooltip>
                              );
                            })}
                          </Box>
                          <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                            <Typography sx={{ fontSize: 11, color: colors.neutral[500], fontFamily: "monospace" }}>14d ago</Typography>
                            <Typography sx={{ fontSize: 11, color: colors.neutral[500], fontFamily: "monospace" }}>today</Typography>
                          </Stack>
                        </Box>
                        {rule.lastFiredAt && (
                          <Typography sx={{ fontSize: 13, color: colors.neutral[800] }}>
                            Most recent fire: <strong>{shortAbs(rule.lastFiredAt)}</strong>
                            <Box component="span" sx={{ color: colors.neutral[600] }}> · {relativeTime(rule.lastFiredAt)}</Box>
                          </Typography>
                        )}
                      </Stack>
                    </SectionCard>
                  )}
                </Stack>
              </Box>
            </ReactPerfectScrollbar>
          </Box>

          {/* Drawer footer */}
          <Stack direction="row" alignItems="center" spacing={1}
            sx={{
              p: 3, borderTop: `1px solid ${colors.neutral[200]}`,
              backgroundColor: "#fff", flexShrink: 0,
            }}>
            {isEdit && !rule.builtin && (
              <Button startIcon={<DeleteIcon />}
                onClick={() => onDelete(rule)}
                sx={{
                  height: 36, borderRadius: "8px", textTransform: "none",
fontSize: 14,
                  color: colors.red[100], borderColor: colors.red[100],
                  "&:hover": { backgroundColor: "#FEF2F2" },
                }}>Delete</Button>
            )}
            <Box sx={{ flex: 1 }} />
            {isEdit && (
              <Button startIcon={<BoltIcon />} variant="outlined"
                onClick={() => onTestFire(rule)}
                disabled={busyId === rule.id}
                sx={{
                  height: 36, borderRadius: "8px", textTransform: "none",
fontSize: 14,
                  color: colors.neutral[700], borderColor: colors.neutral[300],
                  "&:hover": { borderColor: colors.neutral[400], backgroundColor: colors.neutral[50] },
                }}>Test fire</Button>
            )}
            <CancelButton onClick={onClose} sx={{ width: "auto", minWidth: 96 }} />
            <Button
              variant="contained"
              disableElevation
              onClick={onSubmit}
              disabled={saving || !validation.valid}
              sx={{ ...primaryButtonSx, fontWeight: 600 }}
            >
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create rule"}
            </Button>
          </Stack>
        </Box>
      </Slide>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function AlertsPage() {
  const { showSuccess, showError } = useNotification();
  const [rules, setRules] = useState(null);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const [filter, setFilter] = useState({ sev: "all", state: "all", kind: "all", q: "" });
  const [group, setGroup] = useState("severity");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [rulesResp, statsResp] = await Promise.all([
        axios.get("alert/rules"),
        axios.get("alert/rules/stats").catch(() => ({ data: {} })),
      ]);
      setRules(Array.isArray(rulesResp.data) ? rulesResp.data : []);
      setStats(statsResp.data || {});
    } catch (e) {
      showError(e.response?.data?.message || e.message || "Failed to load rules");
      setRules([]);
    } finally { setLoading(false); }
  }, [showError]);

  useEffect(() => { reload(); }, [reload]);

  // Inject the firing-tag pulse keyframe once.
  useEffect(() => {
    if (document.getElementById("alert-pulse-kf")) return;
    const style = document.createElement("style");
    style.id = "alert-pulse-kf";
    style.textContent = `@keyframes alertPulse {
      0%, 100% { box-shadow: 0 0 0 0 rgba(220,38,38,0.4); }
      50%      { box-shadow: 0 0 0 4px rgba(220,38,38,0); }
    }`;
    document.head.appendChild(style);
  }, []);

  const onToggleEnabled = async (rule) => {
    setBusyId(rule.id);
    try {
      const r = await axios.patch(`alert/rules/${rule.id}`, { enabled: !rule.enabled });
      setRules((prev) => prev.map((x) => x.id === rule.id ? r.data : x));
      showSuccess(`Rule ${r.data.enabled ? "enabled" : "disabled"}`);
    } catch (e) {
      showError(e.response?.data?.message || e.message);
    } finally { setBusyId(null); }
  };

  const onDelete = async (rule) => {
    setBusyId(rule.id);
    try {
      await axios.delete(`alert/rules/${rule.id}`);
      setRules((prev) => prev.filter((x) => x.id !== rule.id));
      showSuccess(`Deleted "${rule.name}"`);
      setEditing(null);
    } catch (e) {
      showError(e.response?.data?.message || e.message);
    } finally { setBusyId(null); setConfirmDelete(null); }
  };

  const onTestFire = async (rule) => {
    setBusyId(rule.id);
    try {
      const r = await axios.post(`alert/rules/${rule.id}/test_fire`);
      const ch = (r.data?.channels || []).join(", ") || "none (channels resolved to empty)";
      showSuccess(`Test fired. Channels: ${ch}`);
      try {
        const s = await axios.get("alert/rules/stats");
        setStats(s.data || {});
      } catch (_) { /* ignore */ }
    } catch (e) {
      showError(e.response?.data?.message || e.message || "Test fire failed");
    } finally { setBusyId(null); }
  };

  const onSave = (rule, wasEdit) => {
    setEditing(null);
    if (wasEdit) {
      setRules((prev) => prev.map((x) => x.id === rule.id ? rule : x));
      showSuccess(`Saved "${rule.name}"`);
    } else {
      setRules((prev) => [...(prev || []), rule]);
      showSuccess(`Created "${rule.name}"`);
    }
  };

  const rulesList = rules || [];

  // Precompute the firing-state map once per rules change. Previously every
  // keystroke in the filter input re-ran `isFiring(r)` (which constructs
  // `new Date(r.lastFiredAt)` + `Date.now()`) across all 5 consumers below,
  // so a 30-rule org typing a 20-char query did ~3000 Date allocations on
  // the cold path. Now we materialise the verdict once and look it up O(1)
  // from every consumer.
  const firingMap = useMemo(() => {
    const m = new Map();
    for (const r of rulesList) m.set(r.id, isFiring(r));
    return m;
  }, [rulesList]);

  const filtered = useMemo(() => {
    return rulesList.filter((r) => {
      if (filter.sev !== "all" && r.severity !== filter.sev) return false;
      if (filter.kind === "custom" && r.builtin) return false;
      if (filter.kind === "builtin" && !r.builtin) return false;
      if (filter.state === "firing" && !firingMap.get(r.id)) return false;
      if (filter.state === "disabled" && r.enabled) return false;
      if (filter.q) {
        const q = filter.q.toLowerCase();
        const hay = (r.name + " " + (r.description || "") + " " + r.alertType).toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rulesList, filter, firingMap]);

  const groups = useMemo(() => {
    if (!filtered) return [];
    if (group === "severity") {
      const order = ["critical", "high", "medium", "low", "info"];
      return order
        .map((s) => ({ title: sevSpec(s).label, items: filtered.filter((r) => r.severity === s) }))
        .filter((g) => g.items.length > 0);
    }
    if (group === "kind") {
      return [
        { title: "Custom rules",   items: filtered.filter((r) => !r.builtin) },
        { title: "Built-in rules", items: filtered.filter((r) =>  r.builtin) },
      ].filter((g) => g.items.length > 0);
    }
    if (group === "state") {
      return [
        { title: "Firing now",  items: filtered.filter((r) => firingMap.get(r.id)) },
        { title: "Active",      items: filtered.filter((r) => r.enabled && !firingMap.get(r.id)) },
        { title: "Disabled",    items: filtered.filter((r) => !r.enabled) },
      ].filter((g) => g.items.length > 0);
    }
    return [{ title: null, items: filtered }];
  }, [filtered, group, firingMap]);

  return (
    <>
      <Helmet><title>Alert rules</title></Helmet>
      <Box sx={{ p: 6 }}>
        <Stack spacing={4}>
          {/* Page header — matches auditlogs.js */}
          <Stack spacing={2}>
            <V2PageHeader
              title="Alert rules"
              description="Trigger notifications when WAF, bot, certificate, or anomaly events cross thresholds. Built-in rules cover the basics — write custom rules and route them to in-app, email, or webhook."
              sx={{ pb: 0 }}
            />
          </Stack>

          {loading ? (
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: "12px" }} />
          ) : (
            <Kpis rules={rulesList} stats={stats} firingMap={firingMap} />
          )}

          <FilterBar
            filter={filter}
            setFilter={setFilter}
            group={group}
            setGroup={setGroup}
            onCreate={() => setEditing({})}
          />

          {/* Rules table — standard card container + MUI Table */}
          <Box sx={{
            backgroundColor: "#fff",
            borderRadius: "12px",
            border: `1px solid ${colors.neutral[200]}`,
            overflow: "hidden",
          }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: colors.neutral[50] }}>
                  <TableCell sx={{ ...tableHeadSx, pl: 3 }}>Name &amp; trigger</TableCell>
                  <TableCell sx={tableHeadSx}>Severity</TableCell>
                  <TableCell sx={tableHeadSx}>Channels</TableCell>
                  <TableCell sx={tableHeadSx}>Fire activity</TableCell>
                  <TableCell sx={tableHeadSx}>Last fired</TableCell>
                  <TableCell sx={{ ...tableHeadSx, pr: 3, textAlign: "right" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <TableRow key={`sk-${i}`}>
                      {Array.from({ length: 6 }).map((__, j) => (
                        <TableCell key={j} sx={{ padding: "16px 8px" }}>
                          <Skeleton variant="rectangular" height={22} sx={{ borderRadius: "11px" }} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: "center", py: 6, color: colors.neutral[500] }}>
                      <Typography sx={ { fontSize: 14, color: colors.neutral[700], mb: 0.5 }}>
                        No rules match these filters
                      </Typography>
                      <Typography sx={ { fontSize: 12, color: colors.neutral[500] }}>
                        Try clearing severity or state filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  groups.map((g, gi) => (
                    <React.Fragment key={g.title || `g-${gi}`}>
                      {g.title && (
                        <TableRow>
                          <TableCell colSpan={6} sx={{
                            backgroundColor: colors.neutral[50],
                            borderTop: `1px solid ${colors.neutral[200]}`,
                            borderBottom: `1px solid ${colors.neutral[200]}`,
                            py: 1,
                          }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography sx={ { fontSize: 12, fontWeight: 500, color: colors.neutral[700], textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                {g.title}
                              </Typography>
                              <Typography sx={{ fontFamily: "monospace", fontSize: 11, color: colors.neutral[500] }}>
                                · {g.items.length} {g.items.length === 1 ? "rule" : "rules"}
                              </Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )}
                      {g.items.map((r) => {
                        const firing = firingMap.get(r.id) || false;
                        const sev = sevSpec(r.severity);
                        const stat = stats[r.id];
                        const fires7d  = stat?.fires7d  || 0;
                        const fires24h = stat?.fires24h || 0;
                        return (
                          <TableRow key={r.id}
                            onClick={() => setEditing(r)}
                            sx={{
                              cursor: "pointer",
                              opacity: busyId === r.id ? 0.55 : (r.enabled ? 1 : 0.6),
                              transition: "background-color 0.15s",
                              "&:hover": { backgroundColor: colors.neutral[50] },
                              borderLeft: firing ? `3px solid ${colors.red[100]}` : "3px solid transparent",
                            }}>
                            {/* Name & trigger */}
                            <TableCell sx={{ ...tableBodySx, pl: 3, py: 2 }}>
                              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                                <Typography sx={ { fontSize: 14, fontWeight: 500, color: colors.neutral[1000] }}>
                                  {r.name}
                                </Typography>
                                <KindTag builtin={r.builtin} />
                                {firing && <FiringTag />}
                              </Stack>
                              {r.description && (
                                <Typography sx={ { fontSize: 13, color: colors.neutral[700], mb: 0.5 }}>
                                  {r.description}
                                </Typography>
                              )}
                              <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: colors.neutral[600] }}>
                                  {triggerExpr(r)}
                                </Typography>
                                <Box sx={{ width: 3, height: 3, borderRadius: "50%", backgroundColor: colors.neutral[400] }} />
                                <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>{scopeLabel(r)}</Typography>
                              </Stack>
                            </TableCell>

                            <TableCell sx={{ ...tableBodySx, py: 2 }}>
                              <SeverityChip s={r.severity} />
                            </TableCell>

                            <TableCell sx={{ ...tableBodySx, py: 2 }}>
                              <ChannelsCell list={r.channels} />
                            </TableCell>

                            <TableCell sx={{ ...tableBodySx, py: 2 }}>
                              <Stack spacing={0.5} alignItems="flex-start">
                                <Typography sx={{
fontSize: 14, fontWeight: 500,
                                  color: fires7d === 0 ? colors.neutral[500] : colors.neutral[1000],
                                  fontVariantNumeric: "tabular-nums",
                                }}>{fires7d === 0 ? "—" : fires7d.toLocaleString()}</Typography>
                                <FireSpark data={stat?.sparkline14d} color={fires7d > 0 ? sev.dot : null} />
                                <Typography sx={{
                                  fontSize: 11, color: colors.neutral[600],
                                  textTransform: "uppercase", letterSpacing: "0.05em",
                                }}>7d · {fires24h > 0 ? `${fires24h} today` : "none today"}</Typography>
                              </Stack>
                            </TableCell>

                            <TableCell sx={{ ...tableBodySx, py: 2 }}>
                              {r.lastFiredAt ? (
                                <Stack spacing={0.25}>
                                  <Typography sx={{ fontSize: 13, color: colors.neutral[800], fontVariantNumeric: "tabular-nums" }}>
                                    {shortAbs(r.lastFiredAt)}
                                  </Typography>
                                  <Typography sx={{ fontSize: 12, color: colors.neutral[600] }}>{relativeTime(r.lastFiredAt)}</Typography>
                                </Stack>
                              ) : (
                                <Typography sx={{ fontSize: 13, color: colors.neutral[500] }}>Never fired</Typography>
                              )}
                            </TableCell>

                            <TableCell sx={{ ...tableBodySx, pr: 3, py: 2, textAlign: "right" }}
                              onClick={(e) => e.stopPropagation()}>
                              <Stack direction="row" spacing={0.25} justifyContent="flex-end" alignItems="center">
                                <Switch
                                  checked={r.enabled}
                                  size="small"
                                  onChange={() => onToggleEnabled(r)}
                                  disabled={busyId === r.id}
                                  sx={switchSmallSx}
                                />
                                <Tooltip title="Test fire">
                                  <span>
                                    <IconButton size="small" onClick={() => onTestFire(r)} disabled={busyId === r.id}>
                                      <PlayIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => setEditing(r)}>
                                    <EditIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </Stack>
      </Box>

      {/* Only mount the drawer when it has work to do. Previously the parent
          always rendered <RuleDrawer> and the component returned null inside
          when rule was falsy — that meant the React subtree (Modal, Slide,
          ReactPerfectScrollbar, ConditionsEditor, all the SectionCard +
          form state) was reconciled on every parent re-render even when the
          drawer was closed. Conditional mount drops that work to zero when
          idle. */}
      {editing && (
        <RuleDrawer
          rule={editing}
          stats={editing.id ? stats[editing.id] : null}
          open={true}
          onClose={() => setEditing(null)}
          onSave={onSave}
          onDelete={(r) => setConfirmDelete(r)}
          onTestFire={onTestFire}
          busyId={busyId} />
      )}

      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={ { fontSize: 18, fontWeight: 500, color: colors.neutral[1000] }}>
          Delete alert rule?
        </DialogTitle>
        <DialogContent>
          <Typography sx={ { fontSize: 14, color: colors.neutral[800] }}>
            "{confirmDelete?.name}" will be removed. Existing alert events from this rule stay in the history.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setConfirmDelete(null)}
            sx={{ height: 36, borderRadius: "8px", textTransform: "none", color: colors.neutral[700] }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => onDelete(confirmDelete)}
            sx={{
              height: 36, borderRadius: "8px", textTransform: "none", fontWeight: 600,
              backgroundColor: colors.red[100], color: "#fff",
              "&:hover": { backgroundColor: "#b42318" },
            }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
